import cx from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { UI_CROSS } from '@bwoty-web/ui-kit/icons';
import { HotelCard, Icon, Modal } from '@bwoty-web/ui-kit';
import { useIsGlobetrotter, useIsMobile } from '@bwoty-web/ui-kit/hooks';

import PriceSpec from './PriceSpec';
import {
  checkAndUpdateUrlWithExtendedSearchOptions,
  isCombination,
  standaloneUrlHandler,
} from '../../../utils/url/url';

import './HotelHitWrapper.scss';

const HotelHitWrapper = ({
  data,
  hideButton = false,
  labels,
  mapView = false,
  onClose = () => {},
  onSelect = null,
  position = 0,
  searchInformation,
  siteSettings,
  transportInfo,
}) => {
  const isMobile = useIsMobile();
  const isGlobetrotter = useIsGlobetrotter();
  const isCombinationCheck = isCombination();
  const { imageDomain } = siteSettings;

  const tags = [];
  if (!isGlobetrotter) {
    if (data?.isAdultHotel && data?.isAdultHotelText) {
      tags.push(data?.isAdultHotelText);
    } else if (data?.isFamilyHotel && data?.isFamilyHotelText) {
      tags.push(data?.isFamilyHotelText);
    }
  }

  if (isGlobetrotter && data?.classifications) {
    tags.push(...data.classifications);
  }

  const [showPriceSpec, setShowPriceSpec] = useState(false);

  const renderPriceSpec = () => {
    if (!showPriceSpec) return null;
    return (
      <Modal
        heading={`${labels?.priceDetails?.price_headline} - ${data?.name}`}
        isOpen={showPriceSpec}
        onClose={() => setShowPriceSpec(false)}
      >
        <PriceSpec
          hotel={data}
          searchInformation={searchInformation}
          transportInfo={transportInfo}
          labels={labels.priceDetails}
          imageDomain={imageDomain}
        />
      </Modal>
    );
  };

  const district = data?.geographical?.district;
  const resort = data?.geographical?.resort;
  const area = data?.geographical?.area;

  const showArea = (area && !resort) || (area && resort && resort.name !== area.name);
  const subtitle = ''
    .concat(district ? `${district.name}, ` : '')
    .concat(resort ? `${resort.name}` : '')
    .concat(showArea ? `, ${area.name}` : '');

  const bullets = data?.quickFacts?.map(({ name, value }) => `${name}: ${value}`);

  const packageDescriptionText = data?.boardCodeDescription;
  const packageIncludedText = packageDescriptionText && labels?.mealIncludedLabel;
  const packageDescription = ''
    .concat(packageDescriptionText ? `${data.boardCodeDescription} ` : '')
    .concat(packageIncludedText ? `${labels.mealIncludedLabel} ` : '');

  let ksps = [];
  if (packageDescription) ksps.push(packageDescription);
  if (data?.usps) ksps = ksps.concat(data?.usps);

  const transportText = `${
    transportInfo.luggageIncluded ? labels.luggageIncluded : labels.luggageNotIncluded
  }, ${transportInfo.cabinClass}`;
  const footerText = (
    <>
      {`${labels.productsIncludedParsed} ${searchInformation?.passengerInfo}.`}
      {!isMobile && <br />}
      {`${isMobile ? ' ' : ''}${transportText}.`}
    </>
  );

  const canOpenPriceDetails =
    !mapView && searchInformation && !isCombinationCheck && transportInfo?.outBoundFlights?.[0] && data.priceBreakDown;

  let columnSpan;
  if (mapView) columnSpan = 4;
  else if (isCombinationCheck && position !== 1) columnSpan = 6;

  const linkList =
    isCombinationCheck && data?.url
      ? [{ text: labels?.readMoreHotel, href: `${data.url}?pageTemplate=popUpPage&qf=popUpPage` }]
      : [];

  return (
    <div
      className={cx('iw-hotel-list-item', {
        'iw-hotel-list-item--map': mapView,
      })}
    >
      {mapView && !isMobile && (
        <Icon
          alt="Close selected hotel card"
          className="iw-hotel-list-item-map__close-icon"
          onClick={onClose}
          onKeyUp={(e) => {
            if (e.key === 'Enter') onClose();
          }}
          path={UI_CROSS}
          size="l"
          tabIndex="0"
        />
      )}
      <HotelCard
        bullets={bullets}
        {...(!hideButton && {
          onClick: onSelect,
          url: standaloneUrlHandler(checkAndUpdateUrlWithExtendedSearchOptions(data?.links?.roomSelect)),
        })}
        columnSpan={columnSpan}
        {...(!isGlobetrotter && { concept: data?.concept?.imageClass })}
        {...(isGlobetrotter && !mapView && { description: data?.introText })}
        {...(data?.isAvailable && { footerDescription: footerText })}
        showPriceOnRequest={!data?.isAvailable}
        guestRating={data?.guestRating}
        image={{
          ...data?.hotelImage,
          size: {
            height: data?.hotelImage?.height,
            width: data?.hotelImage?.width,
          },
        }}
        imagePosition={mapView || (isCombinationCheck && position !== 1) ? 'top' : undefined}
        imageWidth={isGlobetrotter ? 6 : 4}
        ksps={ksps.slice(0, isGlobetrotter ? 3 : 1)}
        lolloBernie={false}
        noBackground={mapView && isMobile}
        onClickGallery={() => {
          window.postMessage(`gallery?itemType=hotel&id=${data?.id}`);
          if (isMobile) onClose();
        }}
        imageGalleryCount={data?.imageGalleryCount}
        onClickPriceDetails={canOpenPriceDetails ? () => setShowPriceSpec(true) : undefined}
        originalPrice={data?.priceInfo?.ordinaryPrice}
        price={data?.priceInfo?.totalPrice}
        promotionLabel={{
          text: data?.promoLabel?.text,
          type: data?.promoLabel?.promotionType,
          urgency: data?.promoLabel?.urgency,
        }}
        pricePerPerson={
          searchInformation?.numberOfAdults === 2 && searchInformation?.numberOfChildren === 0
            ? data?.priceInfo?.twoAdultsPricePerPerson
            : undefined
        }
        rating={data?.hotelRating}
        subtitle={subtitle}
        tags={tags}
        title={data?.name}
        tripadvisorRating={data?.tripAdvisor?.averageRating}
        linkList={linkList}
      />
      {!mapView && renderPriceSpec()}
    </div>
  );
};

HotelHitWrapper.propTypes = {
  data: PropTypes.shape().isRequired,
  hideButton: PropTypes.bool,
  mapView: PropTypes.bool,
  onClose: PropTypes.func,
  onSelect: PropTypes.func,
  position: PropTypes.number,
  searchInformation: PropTypes.shape().isRequired,
  siteSettings: PropTypes.shape({
    imageDomain: PropTypes.string.isRequired,
    siteId: PropTypes.number.isRequired,
  }).isRequired,
  labels: PropTypes.shape().isRequired,
  transportInfo: PropTypes.shape().isRequired,
};

export default HotelHitWrapper;
